import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, CheckCircle, ArrowRight, ArrowLeft, AlertCircle, Plus, Minus, User, Lock, X, Mail, Eye, EyeOff, ShoppingCart } from 'lucide-react';
import { useQuery, useQueryClient } from 'react-query';
import SEO from '../components/SEO';
import { programsAPI, registrationAPI, cartAPI, childrenAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import {
  formatScheduleDate,
  formatScheduleTime,
  getProgramScheduleEntries,
  getProgramScheduleRange,
  getProgramScheduleSummary,
  getProgramScheduleDayTime,
} from '../utils/programSchedules';

interface AuthorizedPickupPerson {
  name: string;
  relationship: string;
  phone: string;
}

interface ChildData {
  id?: number;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  grade: string;
  allergies: string;
  special_needs: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relationship: string;
  school: string;
  authorized_pickup_persons: AuthorizedPickupPerson[];
  preferred_hospital: string;
}

const TRACK_LABELS: Record<string, { label: string; icon: string }> = {
  robotics: { label: 'Robotics Engineering', icon: '🤖' },
  cs_ai: { label: 'Computer Science & AI', icon: '💻' },
};

const isValidName = (value: string) => /^[A-Za-z\u00C0-\u00FF'\-.\s]+$/.test(value.trim());
const isValidPhone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
};
const getPasswordErrors = (password: string) => [
  password.length >= 8 ? '' : 'at least 8 characters',
  /[A-Z]/.test(password) ? '' : 'one uppercase letter',
  /[a-z]/.test(password) ? '' : 'one lowercase letter',
  /\d/.test(password) ? '' : 'one number',
].filter(Boolean);

const RegistrationPage: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const maxDob = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const minDob = new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Steps: 1=Program, 2=Children Count, 3=Child Details, 4=Review
  const [step, setStep] = useState(1);
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [selectedLevel, setSelectedLevel] = useState<any>(null);
  const [activeTrack, setActiveTrack] = useState<string>('all');
  const [numberOfChildren, setNumberOfChildren] = useState(1);
  const [children, setChildren] = useState<ChildData[]>([{
    first_name: '', last_name: '', date_of_birth: '', grade: '', allergies: '', special_needs: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '', school: '',
    authorized_pickup_persons: [{ name: '', relationship: '', phone: '' }], preferred_hospital: ''
  }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Terms & Consent state - single checkbox for all policies
  const [allPoliciesAccepted, setAllPoliciesAccepted] = useState(false);
  
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch programs (includes available_sessions)
  const { data: programs = [], isLoading } = useQuery(
    'programs',
    async () => {
      const response = await programsAPI.getPrograms();
      return response.data?.results || response.data || [];
    }
  );

  // Fetch program levels
  const { data: allLevels = [] } = useQuery(
    'program-levels',
    async () => {
      const response = await programsAPI.getLevels();
      return response.data?.results || response.data || [];
    }
  );

  // Fetch program categories
  const { data: categories = [] } = useQuery(
    'program-categories',
    async () => {
      const response = await programsAPI.getCategories();
      return response.data?.results || response.data || [];
    }
  );

  // Get levels for the selected program
  const programLevels = selectedProgram
    ? allLevels.filter((level: any) => level.program === selectedProgram.id && level.is_active)
    : [];

  // Pre-select program from a ?program=<id> link (e.g. from a level-completion email)
  useEffect(() => {
    if (selectedProgram || !programs.length) return;
    const params = new URLSearchParams(location.search);
    const programId = params.get('program');
    if (!programId) return;
    const match = programs.find((p: any) => String(p.id) === programId);
    if (match) {
      setSelectedProgram(match);
      setStep(2);
    }
  }, [programs, location.search, selectedProgram]);

  // Pre-fill the enrolling child from a ?child=<id> link (e.g. from a
  // next-level evaluation email) so the parent doesn't have to retype a
  // child's details for a level they're already enrolled with us on.
  const { data: existingChildren = [] } = useQuery(
    'children-for-prefill',
    async () => {
      const response = await childrenAPI.getChildren();
      return response.data?.results || response.data || [];
    },
    { enabled: isAuthenticated }
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const childId = params.get('child');
    if (!childId || !existingChildren.length) return;
    const match = existingChildren.find((c: any) => String(c.id) === childId);
    if (!match) return;

    setNumberOfChildren(1);
    setChildren([{
      id: match.id,
      first_name: match.first_name || '',
      last_name: match.last_name || '',
      date_of_birth: match.date_of_birth || '',
      grade: match.grade || '',
      allergies: match.allergies || '',
      special_needs: match.special_needs || '',
      emergency_contact_name: match.emergency_contact_name || '',
      emergency_contact_phone: match.emergency_contact_phone || '',
      emergency_contact_relationship: match.emergency_contact_relationship || '',
      school: match.school || '',
      authorized_pickup_persons: match.authorized_pickup_persons?.length
        ? match.authorized_pickup_persons
        : [{ name: '', relationship: '', phone: '' }],
      preferred_hospital: match.preferred_hospital || '',
    }]);
  }, [existingChildren, location.search]);

  // Update children array when numberOfChildren changes
  useEffect(() => {
    const currentLength = children.length;
    if (numberOfChildren > currentLength) {
      const newChildren = [...children];
      for (let i = currentLength; i < numberOfChildren; i++) {
        newChildren.push({
          first_name: '',
          last_name: '',
          date_of_birth: '',
          grade: '',
          allergies: '',
          special_needs: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          emergency_contact_relationship: '',
          school: '',
          authorized_pickup_persons: [{ name: '', relationship: '', phone: '' }],
          preferred_hospital: ''
        });
      }
      setChildren(newChildren);
    } else if (numberOfChildren < currentLength) {
      setChildren(children.slice(0, numberOfChildren));
    }
  }, [numberOfChildren]);

  const handleChildChange = (index: number, field: keyof ChildData, value: string) => {
    const updatedChildren = [...children];
    updatedChildren[index] = { ...updatedChildren[index], [field]: value };
    setChildren(updatedChildren);
  };

  const validateStep = (stepNumber: number): boolean => {
    switch (stepNumber) {
      case 1:
        return selectedProgram !== null;
      case 2:
        // No upper cap - parent can register as many children as needed
        return numberOfChildren >= 1;
      case 3:
        return children.every(child =>
          child.first_name.trim() !== '' &&
          isValidName(child.first_name) &&
          child.last_name.trim() !== '' &&
          isValidName(child.last_name) &&
          child.date_of_birth !== '' &&
          child.date_of_birth >= minDob &&
          child.date_of_birth <= maxDob &&
          child.school.trim() !== '' &&
          child.emergency_contact_name.trim() !== '' &&
          isValidName(child.emergency_contact_name) &&
          child.emergency_contact_phone.trim() !== '' &&
          isValidPhone(child.emergency_contact_phone) &&
          // At least one authorized pickup person with complete info
          child.authorized_pickup_persons.length > 0 &&
          child.authorized_pickup_persons.every((p: AuthorizedPickupPerson) =>
            p.name.trim() !== '' &&
            isValidName(p.name) &&
            p.relationship.trim() !== '' &&
            p.phone.trim() !== '' &&
            isValidPhone(p.phone)
          )
        );
      case 4:
        return allPoliciesAccepted;
      default:
        return true;
    }
  };

  const handleNextStep = async () => {
    if (!validateStep(step)) {
      if (step === 1) toast.error('Please select a program');
      if (step === 2) toast.error('Please specify number of children');
      if (step === 3) toast.error('Please enter valid child details: names must use letters only, dates must be ages 1-18, phone numbers must be 7-15 digits, school is required, and at least one authorized pickup person is required.');
      if (step === 4) toast.error('Please accept the Terms and Conditions to continue');
      return;
    }
    
    // Add to cart instead of going to payment step
    if (step === 4) {
      setIsSubmitting(true);
      try {
        // Add each child as a separate cart item
        for (const child of children) {
          await cartAPI.addItem({
            program_id: selectedProgram.id,
            level_id: selectedLevel?.id || null,
            child_data: child,
          });
        }
        queryClient.invalidateQueries('cart');
        queryClient.invalidateQueries('cart-sidebar');
        queryClient.invalidateQueries('cart-count');
        toast.success(`Added ${children.length} item(s) to cart!`);
        navigate('/cart');
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Failed to add to cart');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    
    setStep(step + 1);
  };

  // Handle joining waitlist for full programs
  const handleJoinWaitlist = async (programId: number) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    try {
      await registrationAPI.joinWaitlist({ program: programId });
      toast.success('You have been added to the waitlist! We will notify you when a spot opens up.');
    } catch (error: any) {
      if (error.response?.data?.error?.includes('already')) {
        toast.error('You are already on the waitlist for this program.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to join waitlist. Please try again.');
      }
    }
  };

  // Handle auth modal submission
  const { login, register } = useAuthStore();
  
  const handleAuthSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    setAuthLoading(true);

    try {
      if (authMode === 'login') {
        const email = formData.get('email') as string;
        const password = formData.get('password') as string;
        await login(email, password);
        toast.success('Logged in successfully!');
        setShowAuthModal(false);
        setStep(step + 1); // Continue to next step (child details)
      } else {
        const data = {
          email: formData.get('email') as string,
          password: formData.get('password') as string,
          password2: formData.get('password2') as string,
          first_name: formData.get('first_name') as string,
          last_name: formData.get('last_name') as string,
          phone: formData.get('phone') as string,
        };
        
        if (data.password !== data.password2) {
          toast.error('Passwords do not match.');
          setAuthLoading(false);
          return;
        }
        if (!isValidName(data.first_name) || !isValidName(data.last_name)) {
          toast.error('Names may only contain letters, spaces, hyphens, apostrophes, and dots.');
          setAuthLoading(false);
          return;
        }
        if (!isValidPhone(data.phone)) {
          toast.error('Enter a valid phone number with 7-15 digits.');
          setAuthLoading(false);
          return;
        }
        const passwordErrors = getPasswordErrors(data.password);
        if (passwordErrors.length > 0) {
          toast.error(`Password must include ${passwordErrors.join(', ')}.`);
          setAuthLoading(false);
          return;
        }
        
        await register(data);
        toast.success('Account created successfully!');
        setShowAuthModal(false);
        setStep(step + 1); // Continue to next step (child details)
      }
    } catch (error: any) {
      const fieldErrors = error.response?.data?.field_errors;
      const errorMsg = fieldErrors
        ? Object.entries(fieldErrors).map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`).join(' | ')
        : error.response?.data?.error || error.response?.data?.detail || error.response?.data?.email?.[0] || 'Authentication failed. Please check your details.';
      toast.error(errorMsg);
    } finally {
      setAuthLoading(false);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const isOneTimeProgram = selectedProgram?.payment_type === 'one_time' || selectedProgram?.is_one_time_payment;
  const selectedProgramPrice = selectedProgram?.effective_price || selectedProgram?.one_time_price || selectedProgram?.monthly_price || selectedProgram?.price || 0;
  const programPrice = selectedProgram?.is_early_bird_eligible 
    ? selectedProgram?.early_bird_price 
    : selectedProgramPrice;
  const subtotal = programPrice * children.length;
  const totalAmount = subtotal;
  
  // Get sessions from selected program's available_sessions
  const programSessions = selectedProgram?.available_sessions || [];

  // Step indicator
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
            s === step ? 'bg-primary-500 text-white' : s < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
          }`}>
            {s < step ? <CheckCircle size={20} /> : s}
          </div>
          {s < 4 && <div className={`w-12 h-1 mx-1 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  const stepLabels = ['Select Program', 'How Many Children', 'Child Details', 'Review & Confirm'];

  return (
    <div className="pt-28 min-h-screen bg-gray-50">
      <SEO 
        title="Register for STEM Classes | Enroll Your Child"
        description="Register your child for robotics, coding, and AI classes at Future Minds Academy."
        keywords="register for robotics class, enroll STEM program, sign up coding class kids"
        canonical="/registration"
        noindex={true}
      />
      <section className="gradient-bg py-12">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center text-white">
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">Class Registration</h1>
            <p className="text-lg text-white/80">{stepLabels[step - 1]}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="container-custom max-w-4xl">
          {step <= 4 && <StepIndicator />}
          
          <AnimatePresence mode="wait">
            {/* Step 1: Select Program */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h2 className="text-2xl font-bold text-center mb-6">Choose a Program</h2>

                {/* Category Tabs - Dynamic from API */}
                {categories.length > 1 && (
                  <div className="flex justify-center mb-8">
                    <div className="inline-flex bg-gray-100 rounded-xl p-1 flex-wrap gap-1">
                      <button
                        onClick={() => setActiveTrack('all')}
                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          activeTrack === 'all' ? 'bg-white shadow-md text-primary-600' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        All Programs
                      </button>
                      {categories.filter((cat: any) => cat.is_active).map((cat: any) => (
                        <button
                          key={cat.id}
                          onClick={() => setActiveTrack(cat.slug)}
                          className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                            activeTrack === cat.slug ? 'bg-white shadow-md text-primary-600' : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <span>{cat.icon || '📚'}</span>
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                  </div>
                ) : programs.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                    <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Programs Available</h3>
                    <p className="text-gray-600 mb-4">Check back soon!</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {programs.filter((p: any) => p.is_active && (activeTrack === 'all' || p.category_name?.toLowerCase().replace(/\s+/g, '-') === activeTrack || (p.track || 'robotics') === activeTrack)).map((program: any) => {
                      const sessions = program.available_sessions || [];
                      const uniqueSessionSchedules = Array.from(
                        new Map(sessions.map((s: any) => [getProgramScheduleDayTime(s) || 'TBD', s])).values()
                      );
                      const scheduleEntries = getProgramScheduleEntries(program);
                      const isOneTime = program.payment_type === 'one_time' || program.is_one_time_payment;
                      const displayPrice = program.effective_price || program.one_time_price || program.monthly_price || program.price;
                      const isSelected = selectedProgram?.id === program.id;
                      const enrolledCount = program.enrolled_count || 0;
                      const spotsLeft = Math.max(0, (program.capacity || 0) - enrolledCount);
                      const isFull = program.capacity && enrolledCount >= program.capacity;
                      return (
                        <div
                          key={program.id}
                          onClick={() => { 
                            if (!isFull) {
                              setSelectedProgram(program); 
                              setSelectedLevel(null); 
                            } else {
                              toast.error(`${program.name} is at full capacity. Please check back later.`);
                            }
                          }}
                          className={`bg-white rounded-xl p-6 border-2 transition-all ${
                            isFull ? 'border-red-200 opacity-75 cursor-not-allowed' :
                            isSelected ? 'border-primary-500 shadow-lg ring-2 ring-primary-200 cursor-pointer' : 'border-gray-200 hover:border-primary-300 hover:shadow-md cursor-pointer'
                          }`}
                        >
                          {isFull && (
                            <div className="bg-red-100 text-red-700 text-xs font-semibold px-3 py-1.5 rounded-lg mb-3 text-center">
                              PROGRAM FULL - Join waitlist below
                            </div>
                          )}
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">{program.name}</h3>
                              {program.tagline && <p className="text-sm text-gray-500 mt-1">{program.tagline}</p>}
                            </div>
                            {isSelected && !isFull && <CheckCircle className="text-primary-500 flex-shrink-0" size={24} />}
                          </div>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{program.description}</p>
                          
                          <div className="flex flex-wrap gap-2 mb-4">
                            <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                              Ages {program.min_age}{program.max_age ? `-${program.max_age}` : '+'}
                            </span>
                            <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">
                              {getProgramScheduleSummary(program)}
                            </span>
                            {isOneTime && (
                              <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full">
                                No monthly subscription
                              </span>
                            )}
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              isFull ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                            }`}>
                              {isFull ? 'FULL' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                            </span>
                          </div>

                          {/* Show scheduled sessions */}
                          {sessions.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-4">
                              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                {isOneTime ? 'Scheduled Class Dates' : 'Scheduled Sessions'}
                              </p>
                              <div className="space-y-1">
                              {isOneTime ? (
                                scheduleEntries.length > 0 && scheduleEntries.map((entry) => (
                                  <div key={entry.key} className="flex items-center text-sm text-gray-700">
                                    <Calendar size={12} className="mr-2 text-gray-400" />
                                    <span>{formatScheduleDate(entry.date)}</span>
                                    <Clock size={12} className="ml-3 mr-1 text-gray-400" />
                                    <span>{formatScheduleTime(entry.startTime)} - {formatScheduleTime(entry.endTime)}</span>
                                  </div>
                                ))
                              ) : (
                                uniqueSessionSchedules.map((s: any) => (
                                  <div key={s.id} className="flex items-center text-sm text-gray-700">
                                    <Calendar size={12} className="mr-2 text-gray-400" />
                                    <span>{getProgramScheduleDayTime(s) || 'TBD'}</span>
                                  </div>
                                ))
                              )}
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <span className="text-2xl font-bold text-primary-600">${displayPrice}</span>
                            <span className="text-sm text-gray-500">{isOneTime ? 'one time' : '/month'}</span>
                          </div>
                          {isOneTime && (
                            <p className="text-xs text-gray-600 mt-2">
                              Pay once for this program. You will not be charged monthly for this enrollment.
                              {getProgramScheduleRange(program) ? ` Program runs ${getProgramScheduleRange(program)}.` : ''}
                            </p>
                          )}

                          {isSelected && !isFull && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setStep(2);
                              }}
                              className="w-full mt-4 min-h-[48px] btn-primary justify-center"
                            >
                              Continue with this program <ArrowRight size={18} className="ml-2" />
                            </button>
                          )}

                          {/* Join Waitlist Button for Full Programs */}
                          {isFull && isAuthenticated && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleJoinWaitlist(program.id);
                              }}
                              className="w-full mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold text-sm"
                            >
                              Join Waitlist
                            </button>
                          )}
                          {isFull && !isAuthenticated && (
                            <p className="text-xs text-center text-gray-500 mt-3">
                              <Link to="/login" className="text-primary-500 hover:underline">Login</Link> to join waitlist
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Level selection removed - admin assigns levels in program description */}
              </motion.div>
            )}

            {/* Step 2: Number of Children */}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="max-w-md mx-auto">
                <div className="bg-white rounded-xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold text-center mb-2">How Many Children?</h2>
                  <p className="text-gray-600 text-center mb-8">
                    Each child will be enrolled in {isOneTimeProgram ? 'the scheduled sessions' : `all ${programSessions.length || 4} monthly sessions`} for {selectedProgram?.name}
                  </p>
                  <div className="flex items-center justify-center space-x-6 mb-8">
                    <button onClick={() => setNumberOfChildren(Math.max(1, numberOfChildren - 1))} className="w-14 h-14 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center" disabled={numberOfChildren <= 1}><Minus size={24} /></button>
                    <span className="text-5xl font-bold text-primary-600 w-20 text-center">{numberOfChildren}</span>
                    <button onClick={() => setNumberOfChildren(numberOfChildren + 1)} className="w-14 h-14 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><Plus size={24} /></button>
                  </div>
                  <p className="text-sm text-gray-500 text-center">Add as many children as needed</p>
                </div>
              </motion.div>
            )}

            {/* Step 3: Child Details */}
            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h2 className="text-2xl font-bold text-center mb-6">Child Information</h2>
                <div className="space-y-6">
                  {children.map((child, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-3"><User size={20} /></div>
                        <h3 className="text-lg font-semibold">Child {index + 1}</h3>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                          <input type="text" value={child.first_name} onChange={(e) => handleChildChange(index, 'first_name', e.target.value)} className="input-field" maxLength={100} pattern="[A-Za-z\u00C0-\u00FF' .-]+" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                          <input type="text" value={child.last_name} onChange={(e) => handleChildChange(index, 'last_name', e.target.value)} className="input-field" maxLength={100} pattern="[A-Za-z\u00C0-\u00FF' .-]+" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth <span className="text-red-500">*</span></label>
                          <input type="date" value={child.date_of_birth} onChange={(e) => handleChildChange(index, 'date_of_birth', e.target.value)} min={minDob} max={maxDob} className="input-field" required />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
                          <select value={child.grade} onChange={(e) => handleChildChange(index, 'grade', e.target.value)} className="input-field">
                            <option value="">Select Grade</option>
                            {['K','1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                              <option key={g} value={g}>{g === 'K' ? 'Kindergarten' : `${g}${g==='1'?'st':g==='2'?'nd':g==='3'?'rd':'th'} Grade`}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">School <span className="text-red-500">*</span></label>
                          <input type="text" value={child.school} onChange={(e) => handleChildChange(index, 'school', e.target.value)} className="input-field" placeholder="School name..." maxLength={200} required />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
                          <input type="text" value={child.allergies} onChange={(e) => handleChildChange(index, 'allergies', e.target.value)} className="input-field" placeholder="List any allergies..." maxLength={500} />
                        </div>
                        <div className="md:col-span-2 border-t pt-4 mt-2">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">Emergency Contact</h4>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name <span className="text-red-500">*</span></label>
                              <input type="text" value={child.emergency_contact_name} onChange={(e) => handleChildChange(index, 'emergency_contact_name', e.target.value)} className="input-field" maxLength={200} pattern="[A-Za-z\u00C0-\u00FF' .-]+" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone <span className="text-red-500">*</span></label>
                              <input type="tel" value={child.emergency_contact_phone} onChange={(e) => handleChildChange(index, 'emergency_contact_phone', e.target.value)} className="input-field" maxLength={20} required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                              <input type="text" value={child.emergency_contact_relationship} onChange={(e) => handleChildChange(index, 'emergency_contact_relationship', e.target.value)} className="input-field" placeholder="e.g. Grandmother" maxLength={100} />
                            </div>
                          </div>
                        </div>
                        
                        {/* Authorized Pickup Persons */}
                        <div className="md:col-span-2 border-t pt-4 mt-2">
                          <h4 className="text-sm font-semibold text-gray-800 mb-1">Authorized Pickup Persons</h4>
                          <p className="text-xs text-red-600 font-medium mb-3">
                            List any family members or adults 18 years or older authorized to pick up your child. We will NOT release your child to any adult who is not listed here.
                          </p>
                          <div className="space-y-3">
                            {(child.authorized_pickup_persons || []).map((person: AuthorizedPickupPerson, pIdx: number) => (
                              <div key={pIdx} className="grid grid-cols-3 gap-2 items-end">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Full Name</label>
                                  <input
                                    type="text"
                                    value={person.name}
                                    onChange={(e) => {
                                      const updated = [...children];
                                      const persons = [...(updated[index].authorized_pickup_persons || [])];
                                      persons[pIdx] = { ...persons[pIdx], name: e.target.value };
                                      updated[index] = { ...updated[index], authorized_pickup_persons: persons };
                                      setChildren(updated);
                                    }}
                                     className="input-field"
                                     placeholder="Full name"
                                     maxLength={100}
                                     pattern="[A-Za-z\u00C0-\u00FF' .-]+"
                                   />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Relationship</label>
                                  <input
                                    type="text"
                                    value={person.relationship}
                                    onChange={(e) => {
                                      const updated = [...children];
                                      const persons = [...(updated[index].authorized_pickup_persons || [])];
                                      persons[pIdx] = { ...persons[pIdx], relationship: e.target.value };
                                      updated[index] = { ...updated[index], authorized_pickup_persons: persons };
                                      setChildren(updated);
                                    }}
                                     className="input-field"
                                     placeholder="e.g. Aunt"
                                     maxLength={100}
                                   />
                                </div>
                                <div className="flex gap-2">
                                  <div className="flex-1">
                                    <label className="block text-xs text-gray-500 mb-1">Phone</label>
                                    <input
                                      type="tel"
                                      value={person.phone}
                                      onChange={(e) => {
                                        const updated = [...children];
                                        const persons = [...(updated[index].authorized_pickup_persons || [])];
                                        persons[pIdx] = { ...persons[pIdx], phone: e.target.value };
                                        updated[index] = { ...updated[index], authorized_pickup_persons: persons };
                                        setChildren(updated);
                                      }}
                                       className="input-field"
                                       placeholder="Phone number"
                                       maxLength={20}
                                     />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = [...children];
                                      const persons = [...(updated[index].authorized_pickup_persons || [])];
                                      persons.splice(pIdx, 1);
                                      updated[index] = { ...updated[index], authorized_pickup_persons: persons };
                                      setChildren(updated);
                                    }}
                                    className="self-end mb-0.5 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...children];
                                const persons = [...(updated[index].authorized_pickup_persons || [])];
                                persons.push({ name: '', relationship: '', phone: '' });
                                updated[index] = { ...updated[index], authorized_pickup_persons: persons };
                                setChildren(updated);
                              }}
                              className="text-sm text-primary-600 hover:text-primary-800 font-medium flex items-center"
                            >
                              <Plus size={16} className="mr-1" /> Add Authorized Person
                            </button>
                          </div>
                        </div>

                        {/* Preferred Hospital */}
                        <div className="md:col-span-2 border-t pt-4 mt-2">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">Medical Information</h4>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Hospital/Medical Facility</label>
                            <input
                              type="text"
                              value={child.preferred_hospital}
                              onChange={(e) => handleChildChange(index, 'preferred_hospital', e.target.value)}
                              className="input-field"
                              placeholder="e.g. WakeMed, Duke Health"
                              maxLength={300}
                            />
                            <p className="text-xs text-gray-500 mt-1">In case of emergency, which facility should we take your child to?</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 4: Review & Confirm */}
            {step === 4 && (
              <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h2 className="text-2xl font-bold text-center mb-6">Review & Confirm</h2>
                
                <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                  <h3 className="font-semibold text-lg mb-4">Program</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-bold text-lg">{selectedProgram?.name}</p>
                    <p className="text-sm text-gray-600 mt-1">{selectedProgram?.description?.slice(0, 150)}...</p>
                    <p className="text-lg font-bold text-primary-600 mt-2">
                      ${programPrice}{isOneTimeProgram ? ' one-time' : '/month'} per child
                    </p>
                    {isOneTimeProgram && (
                      <p className="text-sm text-amber-700 mt-2">
                        This is a one-time payment program. You pay once for this program and will not be charged as a monthly subscription.
                      </p>
                    )}
                  </div>
                </div>

                {/* Sessions included */}
                <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                  <h3 className="font-semibold text-lg mb-4">Sessions Included ({programSessions.length})</h3>
                  <div className="space-y-2">
                    {programSessions.map((s: any) => (
                      <div key={s.id} className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                        <CheckCircle size={16} className="text-green-600 mr-3 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="font-medium text-sm">{s.name}</span>
                          <span className="text-xs text-gray-500 ml-3">
                            {isOneTimeProgram
                              ? `${s.start_date || 'TBD'} | ${s.start_time?.slice(0,5)} - ${s.end_time?.slice(0,5)}`
                              : `${getProgramScheduleDayTime(s) || 'TBD'}`}
                          </span>
                        </div>
                      </div>
                    ))}
                    {programSessions.length === 0 && (
                      <p className="text-sm text-gray-500">Sessions will be assigned after enrollment.</p>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
                  <h3 className="font-semibold text-lg mb-4">Children ({children.length})</h3>
                  <div className="space-y-3">
                    {children.map((child, index) => (
                      <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-3"><User size={20} /></div>
                        <div>
                          <p className="font-medium">{child.first_name} {child.last_name}</p>
                          <p className="text-sm text-gray-500">{child.grade ? `Grade ${child.grade}` : 'Grade not specified'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Price Summary */}
                <div className="bg-primary-50 rounded-xl p-6 border border-primary-200">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-gray-600">
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between items-center">
                      <span className="font-semibold text-lg">Total Due</span>
                      <span className="text-2xl font-bold text-primary-600">
                        ${totalAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {children.length} child(ren) x ${programPrice} {isOneTimeProgram ? 'one-time payment' : 'per month'}
                  </p>
                </div>

                {/* Terms & Conditions Consent - Single Checkbox */}
                <div className="bg-white rounded-xl p-6 shadow-sm mt-6 border-2 border-gray-200">
                  <h3 className="font-semibold text-lg mb-2 flex items-center">
                    <AlertCircle size={20} className="mr-2 text-primary-500" />
                    Terms & Privacy Policy
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Please review and accept our policies before proceeding.
                  </p>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm text-gray-600 max-h-40 overflow-y-auto">
                    <p className="mb-2">By checking the box below, you agree to:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Enrollment terms, payment terms, cancellation & refund policies</li>
                      <li>Liability waiver and emergency medical treatment authorization</li>
                      <li>Health & safety policies including illness guidelines</li>
                      <li>Property & equipment responsibility</li>
                      <li>Code of conduct and behavioral expectations</li>
                      <li>Data privacy policy and COPPA compliance</li>
                    </ul>
                  </div>

                  <label className="flex items-start cursor-pointer group p-4 rounded-lg bg-primary-50 border-2 border-primary-200 hover:border-primary-400 transition-colors">
                    <input type="checkbox" checked={allPoliciesAccepted} onChange={(e) => setAllPoliciesAccepted(e.target.checked)}
                      className="mt-0.5 h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                    <span className="ml-3 text-sm text-gray-700">
                      <span className="font-semibold text-gray-900">I accept all Terms & Conditions and Privacy Policy</span> <span className="text-red-500">*</span>
                      <br />
                      <span className="text-gray-500">I have read and agree to the <Link to="/terms" target="_blank" className="text-primary-600 hover:underline font-medium">Terms & Conditions</Link> and <Link to="/privacy" target="_blank" className="text-primary-600 hover:underline font-medium">Privacy Policy</Link>.</span>
                    </span>
                  </label>

                  {!allPoliciesAccepted && (
                    <p className="mt-4 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                      Please accept the terms and privacy policy to proceed to payment.
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          {step >= 1 && step <= 4 && (
            <div className="flex justify-between mt-8">
              {step > 1 ? (
                <button onClick={handlePrevStep} className="btn-outline flex items-center">
                  <ArrowLeft size={20} className="mr-2" /> Back
                </button>
              ) : <div />}
              
              {step < 4 ? (
                <button 
                  onClick={handleNextStep} 
                  className={`flex items-center ${validateStep(step) ? 'btn-primary' : 'bg-gray-300 text-gray-500 px-6 py-3 rounded-lg cursor-not-allowed'}`}
                  disabled={!validateStep(step)}
                >
                  Continue <ArrowRight size={20} className="ml-2" />
                </button>
              ) : step === 4 ? (
                <button
                  onClick={handleNextStep}
                  className={`flex items-center ${allPoliciesAccepted && !isSubmitting ? 'btn-primary' : 'bg-gray-300 text-gray-500 px-6 py-3 rounded-lg cursor-not-allowed'}`}
                  disabled={!allPoliciesAccepted || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Adding to Cart...
                    </>
                  ) : (
                    <>Add to Cart <ShoppingCart size={20} className="ml-2" /></>
                  )}
                </button>
              ) : null}
            </div>
          )}
        </div>
      </section>

      {/* Auth Modal */}
      <AnimatePresence>
        {showAuthModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAuthModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-bold">{authMode === 'login' ? 'Welcome Back!' : 'Create Account'}</h2>
                <button onClick={() => setShowAuthModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>
              <form onSubmit={handleAuthSubmit} className="p-6 space-y-4">
                {authMode === 'register' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input type="text" name="first_name" required maxLength={100} pattern="[A-Za-z\u00C0-\u00FF' .-]+" className="input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input type="text" name="last_name" required maxLength={100} pattern="[A-Za-z\u00C0-\u00FF' .-]+" className="input-field" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="email" name="email" required className="input-field pl-10" placeholder="you@example.com" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type={showPassword ? "text" : "password"} name="password" required minLength={8} autoComplete="new-password" className="input-field pl-10 pr-10" placeholder="********" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {authMode === 'register' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                      <input type="password" name="password2" required minLength={8} autoComplete="new-password" className="input-field" placeholder="********" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                      <input type="tel" name="phone" required maxLength={20} className="input-field" placeholder="(123) 456-7890" />
                    </div>
                  </>
                )}
                <button type="submit" disabled={authLoading} className="btn-primary w-full justify-center">
                  {authLoading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> 
                    : authMode === 'login' ? 'Login & Continue' : 'Create Account & Continue'}
                </button>
                <p className="text-center text-sm text-gray-600">
                  {authMode === 'login' ? (
                    <>Don't have an account? <button type="button" onClick={() => setAuthMode('register')} className="text-primary-600 font-semibold hover:underline">Sign up</button></>
                  ) : (
                    <>Already have an account? <button type="button" onClick={() => setAuthMode('login')} className="text-primary-600 font-semibold hover:underline">Login</button></>
                  )}
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegistrationPage;
